/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ITsriEndpointRecoveryListener } from "../../model/interfaces";
import { Locale, LocaleKeys } from "../../model/locale";
import { Log } from "../../model/logging";
import { TsriEndpointResolver } from "../../model/tsri";
import { formatRetryErrorMessage } from "../../model/utility";
import { dismissAlertOverlayIfOwned, showAlertOverlay } from "../alertOverlay";
import type { UnityAppDispatch } from "../UnityAppStore";

/** Action id recorded when recovery succeeded and the overlay self-dismissed. */
const RECOVERED_ACTION_ID: string = "tsri-endpoint-recovered";

/**
 * Owner tag for this bridge's overlay.
 *
 * Endpoint recovery runs in the background, on screens that mount while the
 * post-sign-in sequence is still going — `ProtectedRoute` renders Home/Player
 * as soon as the user is authenticated, without waiting for startup. An
 * untagged dismiss here would clear whatever overlay happened to be up,
 * including the CRITICAL-retry overlay `postSigninStartupSequence` uses to
 * block on `deviceFeatures`.
 */
const OVERLAY_OWNER: string = "tsri-endpoint-recovery";

/** The single action offered once retrying is known to be pointless. */
const ACKNOWLEDGE_ACTION = { id: "ok", label: "OK" };

/**
 * Bridge between the model-layer `TsriEndpointResolver` and the Redux
 * `alertOverlay` slice.
 *
 * The resolver cannot dispatch: `src/model/` importing `src/viewModel/` is a
 * zero-tolerance layering violation (docs/MVVM-LAYERING-GATE.md, rule
 * `model-to-upper`). So it emits `ITsriEndpointRecoveryListener` callbacks and
 * this bridge — which lives on the ViewModel side of the arrow and may import
 * both — turns them into overlay actions. Same arrangement as
 * `NetworkAvailabilityBridge`.
 *
 * Only critical services reach these callbacks; the resolver keeps non-critical
 * recovery silent by design.
 *
 * ## Why the outstanding set
 *
 * Several critical services can be missing at once — a truncated TSRI response
 * during post-sign-in startup misses them in a burst. Each recovers on its own
 * schedule, so dismissing on the first success would tear the overlay down
 * while the app is still blocked on the others. The overlay is therefore
 * reference-counted: raised when the set becomes non-empty, dropped only when
 * it empties.
 *
 * Lifecycle mirrors `NetworkAvailabilityBridge`: construct, `start()`,
 * `stop()`.
 */
class TsriEndpointRecoveryBridge implements ITsriEndpointRecoveryListener {
  private static readonly TAG: string = "TsriEndpointRecoveryBridge";

  private readonly mResolver: TsriEndpointResolver;

  private readonly mDispatch: UnityAppDispatch;

  /** Critical services currently recovering. Drives the overlay's lifetime. */
  private readonly mOutstanding: Set<string> = new Set();

  private mIsActive: boolean = false;

  /**
   * @param resolver - The model-layer endpoint resolver to observe
   * @param dispatch - The Redux store dispatch function
   */
  constructor(resolver: TsriEndpointResolver, dispatch: UnityAppDispatch) {
    this.mResolver = resolver;
    this.mDispatch = dispatch;
  }

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────────

  public start(): void {
    if (this.mIsActive) {
      Log.w(
        TsriEndpointRecoveryBridge.TAG,
        "start() bridge is already active — ignoring"
      );
      return;
    }
    Log.i(TsriEndpointRecoveryBridge.TAG, "start() subscribing to resolver");
    this.mResolver.addRecoveryListener(this);
    this.mIsActive = true;
  }

  public stop(): void {
    if (!this.mIsActive) {
      Log.w(
        TsriEndpointRecoveryBridge.TAG,
        "stop() bridge is not active — ignoring"
      );
      return;
    }
    Log.i(TsriEndpointRecoveryBridge.TAG, "stop() unsubscribing from resolver");
    this.mResolver.removeRecoveryListener(this);
    this.mIsActive = false;
    this.mOutstanding.clear();
  }

  // ──────────────────────────────────────────────────────────────────
  // ITsriEndpointRecoveryListener
  // ──────────────────────────────────────────────────────────────────

  /**
   * Raises the blocking overlay. Actionless, matching the CRITICAL-retry
   * overlays in `appStartupSlice` and `postSigninStartupSlice`: there is
   * nothing for the user to do while the catalog is re-fetched.
   */
  public onEndpointRecoveryStarted(serviceName: string): void {
    Log.i(
      TsriEndpointRecoveryBridge.TAG,
      `onEndpointRecoveryStarted() ${serviceName} — showing overlay`
    );
    this.mOutstanding.add(serviceName);
    this.mDispatch(
      showAlertOverlay({
        title: Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_TITLE
        ),
        message: Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        ),
        actions: [],
        owner: OVERLAY_OWNER
      })
    );
  }

  public onEndpointRecovered(serviceName: string): void {
    this.mOutstanding.delete(serviceName);
    if (this.mOutstanding.size > 0) {
      Log.d(
        TsriEndpointRecoveryBridge.TAG,
        `onEndpointRecovered() ${serviceName} — ` +
          `${String(this.mOutstanding.size)} still recovering, overlay stays`
      );
      return;
    }
    Log.i(
      TsriEndpointRecoveryBridge.TAG,
      `onEndpointRecovered() ${serviceName} — dismissing overlay`
    );
    this.mDispatch(
      dismissAlertOverlayIfOwned({
        owner: OVERLAY_OWNER,
        dismissedByActionId: RECOVERED_ACTION_ID
      })
    );
  }

  /**
   * The retry budget ran out. Swap the actionless overlay for a dismissible
   * one so the user is not held behind a modal that will never clear on its
   * own — a service the backend has not registered will not appear however
   * long the app waits.
   *
   * Deferred while other services are still recovering: their blocking overlay
   * is the more accurate thing to show, and whichever settles last owns the
   * final state.
   */
  public onEndpointRecoveryFailed(serviceName: string, error: unknown): void {
    this.mOutstanding.delete(serviceName);
    if (this.mOutstanding.size > 0) {
      Log.w(
        TsriEndpointRecoveryBridge.TAG,
        `onEndpointRecoveryFailed() ${serviceName} — ` +
          `${String(this.mOutstanding.size)} still recovering, overlay stays`
      );
      return;
    }
    Log.e(
      TsriEndpointRecoveryBridge.TAG,
      `onEndpointRecoveryFailed() ${serviceName} — showing dismissible overlay`,
      error
    );
    this.mDispatch(
      showAlertOverlay({
        title: Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_TITLE
        ),
        message: formatRetryErrorMessage(error),
        actions: [ACKNOWLEDGE_ACTION],
        owner: OVERLAY_OWNER
      })
    );
  }
}

export { TsriEndpointRecoveryBridge };
